import React, { createContext, useContext, useEffect, useState } from "react";
import { useCallback } from "react";
import { useRouteMatch, useLocation } from "react-router-dom";
import { useIntl } from "react-intl";

import { getOperationById } from "../api";
import operationSteps from "../config/operationSteps";

const OperationContext = createContext({});

const OperationProvider = ({ children }) => {
  const { formatMessage } = useIntl();
  const { search } = useLocation();

  const routeMatch = useRouteMatch("/edit-operation/:id");
  const id = routeMatch?.params?.id;

  const isNewOperation = !routeMatch?.isExact;

  const [stepsData, setStepsData] = useState({});
  const [operationStartFrom, setOperationStartFrom] = useState(
    new URLSearchParams(search).get("startFrom")
  );
  const [operation, setOperation] = useState({});
  const [actualStep, setActualStep] = useState(0);
  const [loadingSave, setLoadingSave] = useState(false);
  const [stepsFilled, setStepsFilled] = useState(() => {
    if (id) {
      return ["dados-da-operacao"];
    }

    return [];
  });

  useEffect(() => {
    getOperationById(id).then(res => {
      const result = res.data.result ?? null;

      if (!result) return;

      const startFrom =
        result.B3Proposta.detalhes_json?.proposta_inicio_tipo ?? "car";

      let requisicaoJson = {};
      if (result.B3Proposta.detalhes_json) {
        requisicaoJson = JSON.parse(
          result.B3Proposta.detalhes_json?.requisicao ?? {}
        );
      }

      const operation = {
        ...result.B3Proposta,
        start_from: startFrom,
        requisicao: requisicaoJson
      };

      setOperation(operation);
      setOperationStartFrom(startFrom);
    });
  }, [id]);

  const canStepBeShown = useCallback(
    index =>
      operationSteps[index]?.allowedOnStartTypes.includes(operationStartFrom),
    [operationStartFrom]
  );

  const previousStep = useCallback(
    (additionalStep = 0) => {
      if (actualStep === 0) return;

      const goTo = actualStep - 1 - additionalStep;
      if (!canStepBeShown(goTo)) {
        previousStep(additionalStep + 1);
        return;
      }

      setActualStep(goTo);
    },
    [actualStep, canStepBeShown]
  );

  const nextStep = useCallback(
    async (additionalStep = 0, setError) => {
      if (actualStep >= operationSteps.length - 1) {
        return;
      }

      const goTo = actualStep + 1 + additionalStep;
      if (!canStepBeShown(goTo)) {
        nextStep(additionalStep + 1, setError);
        return;
      }

      if (!operation?.id) {
        const step = operationSteps[actualStep];
        if (step.saveHandler) {
          setLoadingSave(true);

          try {
            const response = await step.saveHandler({
              formatMessage,
              data: {
                ...stepsData[step.value]
              },
              startFrom: operationStartFrom
            });

            if (response?.error) {
              setError(response.error);
              setLoadingSave(false);
              return;
            }

            const b3Proposta = {
              id: response.data.b3_proposta_id
            };

            setOperation(b3Proposta);
            setLoadingSave(false);
            setStepsFilled(oldStepsFilled => [
              ...oldStepsFilled,
              "dados-da-operacao"
            ]);
          } catch (e) {
            setError(formatMessage({ id: "erro-api" }));
            setLoadingSave(false);
            return;
          }
        }
      }

      setActualStep(goTo);
    },
    [
      actualStep,
      canStepBeShown,
      operation.id,
      formatMessage,
      stepsData,
      operationStartFrom
    ]
  );

  const updateFields = dataToChange => {
    const step = operationSteps[actualStep];
    let data = stepsData[step.value];

    if (!data || Array.isArray(data)) data = {};

    const fieldsToChange = dataToChange.map(change => ({
      [change.field]: change.value
    }));

    const updatedData = Object.assign(data, ...fieldsToChange);

    setStepsData(oldSteps => {
      const tmp = { ...oldSteps };
      tmp[step.value] = updatedData;
      return tmp;
    });

    return updatedData;
  };

  return (
    <OperationContext.Provider
      value={{
        isNewOperation,
        nextStep,
        previousStep,
        actualStep,
        setActualStep,
        stepsFilled,
        stepsData,
        updateFields,
        loadingSave,
        operation,
        operationStartFrom
      }}
    >
      {children}
    </OperationContext.Provider>
  );
};

function useOperation() {
  const context = useContext(OperationContext);

  if (!context) {
    throw new Error("useOperation must be used within a OperationProvider");
  }

  return context;
}

export { useOperation, OperationProvider };
